import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  TextField,
  Checkbox,
  Input,
} from '@mui/material';
import moment, { type Moment } from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { editMission, getAllCompanies, uploadFile } from '../../../api/apis';
import { type Company, type Mission } from '../../../utils/type';
import CustomDatePicker from '../../FormItem/CustomDatePicker';
import NumberInput from '../../FormItem/NumberInput';
import Select from '../../FormItem/Select';
import CircleLine from '../../Progress/CircleLine';
import { ReactComponent as BoxArrowLeft } from '../../../assets/box-arrow-left.svg';
import { dialogSx } from './styles';
import { useHistory } from 'react-router-dom';
import { TokenExpiredError } from '../../../api/error';
import { ROUTES } from '../../../utils/constant';
import { checkTextareaLength } from '../AddMission';

interface StepFormData {
  image: any;
  description: string;
  videoUrl: string;
}

interface FormData {
  title: string;
  score: number;
  hour: number;
  minute: number;
  description: string;
  companyId: string;
  departments: string[];
  end: Moment;
  steps: StepFormData[];
}

export default function EditMission ({
  show,
  mission,
  onClose,
  onSuccess,
}: {
  show: boolean;
  mission: Mission;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState(0);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [page, setPage] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    score: 10,
    hour: 0,
    minute: 0,
    description: '',
    companyId: '',
    departments: [],
    end: moment(),
    steps: [{ image: null, description: '', videoUrl: '' }],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAllCompanies().then(res => {
      if (res.status === 200) {
        const companyList: Company[] = Array.isArray(res.data.company) ? res.data.company : [res.data.company];
        setCompanies(companyList);
        setFormData(prev => {
          return { ...prev, companyId: companyList[0].cid };
        });
      }
    });
  }, []);

  useEffect(() => {
    if (mission) {
      setSelectedDepartments([...mission.departments]);
      setFormData(prev => {
        return {
          ...prev,
          title: mission.title.en,
          score: mission.score,
          hour: moment(mission.end).hour(),
          minute: moment(mission.end).minute(),
          description: mission.description,
          departments: mission.departments,
          end: moment(mission.end),
          steps: mission.steps.map(step => {
            return {
              image: null,
              description: step.description.en,
              videoUrl: step.video_url,
            };
          }),
        };
      });
    }
  }, [mission]);

  const handleSubmit = () => {
    setIsSubmitted(true);
    formData.end.set({ hour: formData.hour, minute: formData.minute, second: 0, millisecond: 0 });
    const promises = [];
    formData.steps.forEach(step => {
      promises.push(uploadFile(step.image));
    });
    const missionData = {
      id: mission.id,
      title: { en: formData.title },
      score: formData.score,
      start: moment().toISOString(),
      end: formData.end.toISOString(),
      description: formData.description,
      departments: formData.departments,
      steps: formData.steps.map(step => {
        return {
          image_url: '',
          description: { en: step.description },
          video_url: step.videoUrl,
          type: 'image',
        };
      }),
    };

    Promise.all(promises).then(res => {
      for (let i = 0; i < res.length; i++) {
        if (res[i]) {
          missionData.steps[i].image_url = res[i].data.file_url;
        } else {
          missionData.steps[i].image_url = mission.steps[i].image_url;
        }
      }
      editMission(missionData)
        .then(res => {
          if (res.status === 200) {
            setIsSubmitted(false);
            if (res.data.status === '1') {
              setCurrentStep(0);
              setFormData({
                title: '',
                score: 10,
                hour: 0,
                minute: 0,
                description: '',
                companyId: companies[selectedCompany].cid,
                departments: [],
                end: moment(),
                steps: [{ image: null, description: '', videoUrl: '' }],
              });
              setSelectedDepartments([]);
              setPage(0);
              onSuccess();
            } else {
              alert(res.data.status);
            }
          }
        })
        .catch(err => {
          if (err instanceof TokenExpiredError) {
            history.push(ROUTES.login);
          }
        });
    });
  };

  const pages = [
    <>
      <Box display="flex" alignItems="center">
        <label>{t('Mission name')}</label>
        <TextField
          fullWidth={true}
          onChange={event => {
            event.persist();
            setFormData(prev => {
              return { ...prev, title: event.target.value };
            });
          }}
          value={formData.title}
        />
      </Box>
      <Box display="flex" alignItems="center">
        <Box display="flex" alignItems="center">
          <label>{t('End Date')}</label>
          <CustomDatePicker
            value={formData.end}
            onChange={val => {
              setFormData(prev => {
                return { ...prev, end: val };
              });
            }
            }
          />
        </Box>
        <Box display="flex" alignItems="center">
          <label>{t('End Time')}</label>
          <NumberInput
            value={formData.hour}
            step={1}
            max={23}
            onChange={val => {
              setFormData(prev => {
                return { ...prev, hour: val };
              });
            }
            }
          />
          <NumberInput
            value={formData.minute}
            step={1}
            max={59}
            onChange={val => {
              setFormData(prev => {
                return { ...prev, minute: val };
              });
            }
            }
          />
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        <label>{t('Score')}</label>
        <NumberInput
          value={formData.score}
          step={10}
          min={10}
          max={100}
          onChange={val => {
            setFormData(prev => {
              return { ...prev, score: val };
            });
          }
          }
        />
      </Box>
      <Box display="flex">
        <label>{t('Description')}</label>
        <Box position="relative" display="flex" alignItems="center" flexGrow="1">
          <TextField
            fullWidth
            multiline
            rows={4}
            onChange={event => {
              event.persist();
              setFormData(prev => {
                return { ...prev, description: event.target.value };
              });
            }}
            value={formData.description}
          />
          <span
            style={{
              position: 'absolute',
              bottom: '10px',
              fontSize: '18px',
              color: checkTextareaLength(formData.description) ? '#006EC2' : '#FF0000',
              right: '10px',
            }}
          >
            {t('Max 100 words')}
          </span>
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        <label>{t('Company')}</label>
        <Select
          menuItems={companies.map(company => {
            return {
              value: company.cid,
              label: company.company_name,
            };
          })}
          onChange={(value: string) => {
            const id: number = companies.findIndex(company => company.cid === value);
            setSelectedCompany(id);
            setFormData(prev => {
              return { ...prev, companyId: value };
            });
          }}
          value={formData.companyId}
        />
      </Box>
      <Box display="flex">
        <label>{t('Department')}*</label>
        <Box display="flex" flex="1" flexWrap="wrap" columnGap="30px">
          {companies[selectedCompany]?.departments.map((department: string) => (
            <Box key={department} display="flex" alignItems="center">
              <Checkbox
                checked={selectedDepartments.includes(department)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  event.persist();
                  const isChecked = event.target.checked;
                  if (isChecked) {
                    setSelectedDepartments(prev => [...prev, department]);
                    setFormData(prev => {
                      return { ...prev, departments: [...prev.departments, department] };
                    });
                  } else {
                    setSelectedDepartments(prev => prev.filter(item => item !== department));
                    setFormData(prev => {
                      return { ...prev, departments: prev.departments.filter(item => item !== department) };
                    });
                  }
                }}
                sx={{
                  color: '#006EC2',
                  paddingLeft: '0',
                }}
              />
              <span className="checkbox-label">{department}</span>
            </Box>
          ))}
        </Box>
      </Box>
    </>,
    <>
      <Box display="flex" alignItems="center" gap="12px">
        <CircleLine
          currentStep={formData.steps.length - 1}
          stepNumber={6}
          setStep={number => {
            if (number > formData.steps.length - 1) {
              const steps = [];
              for (let i = formData.steps.length - 1; i < number; i++) {
                steps.push({ image: null, description: '', videoUrl: '' });
              }
              setFormData(prev => {
                return { ...prev, steps: [...prev.steps, ...steps] };
              });
            }
            setCurrentStep(number);
          }}
          addMode={true}
        />
        {formData.steps.length > 1 && (
          <span
            onClick={() => {
              const steps = formData.steps;
              steps.splice(currentStep, 1);
              setFormData(prev => {
                return { ...prev, steps };
              });

              setCurrentStep(number => Math.max(0, number - 1));
            }}
            style={{ color: '#006EC2', fontSize: '18px', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Remove step {currentStep + 1}
          </span>
        )}
      </Box>

      <Box display="flex" alignItems="center">
        <label>{t('Upload image')}</label>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          startIcon={<BoxArrowLeft width="15px" />}
          sx={{
            width: '125px',
            height: '35px',
            textTransform: 'none',
            fontSize: '18px',
            background: '#006EC2',
            color: '#FFFFFF',
          }}
        >
          {t('Select')}
        </Button>
        <Input
          type="file"
          inputRef={fileInputRef}
          sx={{ display: 'none' }}
          onChange={(e: any) => {
            const steps = formData.steps;
            steps[currentStep].image = e.target.files[0];
            setFormData(prev => {
              return { ...prev, steps };
            });
          }}
        />
        {formData.steps[currentStep].image !== null && (
          <>
            <span style={{ marginLeft: '10px' }}>{formData.steps[currentStep].image?.name || ''}</span>
            <span
              style={{
                marginLeft: '10px',
                color: '#006EC2',
                fontSize: '18px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                const steps = formData.steps;
                steps[currentStep].image = null;
                setFormData(prev => {
                  return { ...prev, steps };
                });
              }}
            >
              {t('Remove')}
            </span>
          </>
        )}
      </Box>
      <Box display="flex">
        <label>{t('Description')}</label>
        <Box position="relative" display="flex" alignItems="center" flexGrow="1">
          <TextField
            fullWidth
            multiline
            rows={4}
            onChange={event => {
              event.persist();
              const steps = formData.steps;
              steps[currentStep].description = event.target.value;
              setFormData(prev => {
                return { ...prev, steps };
              });
            }}
            value={formData.steps[currentStep].description}
          />
          <span
            style={{
              position: 'absolute',
              bottom: '10px',
              fontSize: '18px',
              color: checkTextareaLength(formData.steps[currentStep].description) ? '#006EC2' : '#FF0000',
              right: '10px',
            }}
          >
            {t('Max 100 words')}
          </span>
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        <label>{t('Video URL')}</label>
        <TextField
          fullWidth={true}
          onChange={event => {
            event.persist();
            const steps = formData.steps;
            steps[currentStep].videoUrl = event.target.value;
            setFormData(prev => {
              return { ...prev, steps };
            });
          }}
          value={formData.steps[currentStep]?.videoUrl}
        />
      </Box>
    </>,
  ];
  return (
    <Dialog open={show} sx={dialogSx}>
      <DialogTitle>
        {t('Edit mission')}
        <i className="bi bi-x" onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap="40px">
          {pages[page]}
        </Box>
      </DialogContent>
      <DialogActions>
        {page === 0
          ? (
          <Button
            onClick={() => { setPage(1); }}
            disabled={
              !(
                formData.title &&
                formData.description &&
                checkTextareaLength(formData.description) &&
                formData.departments.length
              )
            }
          >
            {t('Save')}
          </Button>
            )
          : (
          <>
            <Button onClick={() => { setPage(0); }}>{t('Return')}</Button>
            <Button
              onClick={handleSubmit}
              disabled={
                formData.steps.some(step => !step.description || !checkTextareaLength(step.description)) || isSubmitted
              }
            >
              {isSubmitted ? 'Submitting' : t('Confirm')}
            </Button>
          </>
            )}
      </DialogActions>
    </Dialog>
  );
}
